/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { TableProps } from "interfaces";
import { CaseInterface, PatientInterface, QueryResultInterface, HeaderEnum } from "medicalsystem-shared";
import { Dispatch, FC, SetStateAction, useEffect, useState } from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";

import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import Box from "components/generals/Box";
import {
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Modal } from "components/generals/Modal";
import CaseForm from "components/forms/CaseForm";
import { FormActionType } from "types";
import Button from "components/generals/Button";
import { getNewCaseDataContent } from "utils/model.utils";
import { useMaterialUIController } from "context/ThemeContext";

/**
 * Extended properties for the CaseTable component.
 * 
 * @interface CaseTableInterface
 * @extends {TableProps<CaseTableInterface>}
 * @property {Partial<PatientInterface>} [patientSelected] - Currently selected patient
 * @property {Function} [setMedicalCaseList] - Callback to update medical case list
 */
interface CaseTableInterface extends TableProps<CaseTableInterface> {
  patientSelected?: Partial<PatientInterface>;
  setMedicalCaseList?: Dispatch<SetStateAction<CaseInterface[]>>;
}

/**
 * Medical case table component for displaying and managing patient cases.
 * 
 * Features:
 * - Displays patient medical cases in native Material-UI table
 * - View case details via modal with CaseForm
 * - Create new case with "Add New Case" button
 * - Auto-loads cases when patient is selected
 * - Supports dark mode via theme controller
 * - Fixed height table (250px) with scrolling
 * - Links cases to specific patient
 * 
 * Medical Case Types:
 * - Chronic conditions
 * - Acute illnesses
 * - Injury cases
 * - Long-term treatments
 * - Care management programs
 * 
 * Workflow:
 * 1. Patient selected triggers case loading
 * 2. API fetches all cases for patient
 * 3. Cases displayed in table
 * 4. User can view/edit existing or add new case
 * 5. Modal opens with CaseForm for interactions
 * 
 * @param {CaseTableInterface} props - Component properties
 * @param {Partial<PatientInterface>} props.patientSelected - Patient to show cases for
 * @param {Function} props.setMedicalCaseList - Callback to update case list
 * 
 * @returns {JSX.Element} Medical case table with case management
 */
const CaseTable: FC<CaseTableInterface> = ({ patientSelected, setMedicalCaseList }) => {
  const [_isLoading, _error, sendRequest] = useHttp();
  const [caseList, setCaseList] = useState<CaseInterface[]>([]);
  const [activeEditCaseModal, setActiveEditCaseModal] = useState<boolean>(false);
  const [selectedCase, setSelectedCase] = useState<CaseInterface>();
  const [actionType, setActionType] = useState<FormActionType>(FormActionType.NEW);
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  useEffect(() => {
    if (patientSelected) {
      sendRequest(
        CoreAPI_URLs.MEDICAL_CASE_GET_ALL,
        onGetCaseHandler,
        "GET",
        null,
        [{ header: HeaderEnum.PATIENT_ID, value: patientSelected.id }]
      );
    }
  }, [patientSelected]);

  const onGetCaseHandler = (rquestList: QueryResultInterface<CaseInterface>) => {
    if (rquestList.results.length > 0) {
      setCaseList(rquestList.results);
    }
  };

  const openModalHandler = async (medicalCase: CaseInterface) => {
    setSelectedCase(medicalCase);
    setActiveEditCaseModal(true);
  };

  const handleNewCase = () => {
    const newCase = getNewCaseDataContent();
    newCase.patient = patientSelected;
    openModalHandler(newCase);
    setActionType(FormActionType.NEW);
  };

  return (
    <Box sx={{ width: "100%", paddingTop: "2rem" }}>
      <Grid container spacing={2}>
        <Grid item xs={12} paddingBottom={"1rem"}>
          <Button id="cy-create-medical-case" type="button" variant="contained" onClick={handleNewCase}>
            {t("caseTable.createNewMedicalCase")}
          </Button>
        </Grid>
        <Grid item xs={12}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead
                sx={{
                  display: "table-header-group",
                  "& .MuiTableCell-head": { color: darkMode ? "#fff" : "#000" },
                }}
              >
                <TableRow>
                  <TableCell align="center">{t("caseTable.label")}</TableCell>
                  <TableCell align="center">{t("caseTable.type")}</TableCell>
                  <TableCell align="center">{t("caseTable.status")}</TableCell>
                  <TableCell align="center">{t("caseTable.manager")}</TableCell>
                  <TableCell align="center">{t("caseTable.action")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody
                sx={{
                  display: "table-row-group",
                  "& .MuiTableCell-root": {
                    color: darkMode ? "#fff" : "#000",
                  },
                }}
              >
                {caseList.map((medicalCase) => (
                  <>
                  <TableRow key={medicalCase.id}>
                    <TableCell align="center">{medicalCase.label}</TableCell>
                    <TableCell align="center">{medicalCase.typeCase}</TableCell>
                    <TableCell align="center">{medicalCase.status}</TableCell>
                    <TableCell align="center">
                      {medicalCase.provider?.person?.name||""} {medicalCase.provider?.person?.lastname||""}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        type="button"
                        onClick={() => {
                          setActionType(FormActionType.MODIFY);
                          openModalHandler(medicalCase);
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow></>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
      <Modal
        open={activeEditCaseModal}
        onClose={() => {
          setActiveEditCaseModal(false);
        }}
      >
        <CaseForm
          isModal={true}
          modelList={caseList}
          setModelList={setCaseList}
          model={selectedCase}
          action={actionType}
          onCloseAction={() => setActiveEditCaseModal(false)}
        />
      </Modal>
    </Box>
  );
};

export default CaseTable;
